import { schemes, zSchema } from "../../schema-validation";
import {
  EPaymentItemType,
  isRateBasedPaymentType,
  isFixedAmountPaymentType,
} from "../EPaymentItemType";
import { EServiceTypeRateUnit } from "../ServiceTypeRate";

// Shared validation logic for payment items
const paymentItemSuperRefine = (
  data: {
    payment_type?: EPaymentItemType;
    rate?: number;
    gross_amount?: number;
    quantity?: number;
    bill_rate?: number;
    bill_amount?: number;
    work_info?: {
      serviceTypeID?: string;
      workLocationID?: string;
      customerAccountID?: string;
      department_id?: string;
      work_description?: string;
      service_date?: Date;
    };
  },
  ctx: zSchema.RefinementCtx
) => {
  // If payment_type is provided, validate according to it.
  if (data.payment_type) {
    if (isRateBasedPaymentType(data.payment_type)) {
      if (!data.rate || data.rate <= 0) {
        ctx.addIssue({
          code: zSchema.ZodIssueCode.custom,
          message: `Rate is required and must be greater than 0 for payment_type "${data.payment_type}"`,
          path: ["rate"],
        });
      }
      // Bill amount is not allowed for rate-based payment types
      if (data.bill_amount) {
        ctx.addIssue({
          code: zSchema.ZodIssueCode.custom,
          message: `Bill amount is not allowed for rate-based payment types`,
          path: ["bill_amount"],
        });
      }
    } else if (isFixedAmountPaymentType(data.payment_type)) {
      if (!data.gross_amount || data.gross_amount <= 0) {
        ctx.addIssue({
          code: zSchema.ZodIssueCode.custom,
          message: `Gross amount is required and must be greater than 0 for payment_type "${data.payment_type}"`,
          path: ["gross_amount"],
        });
      }
      // Bill rate is not allowed for fixed amount payment types
      if (data.bill_rate) {
        ctx.addIssue({
          code: zSchema.ZodIssueCode.custom,
          message: `Bill rate is not allowed for fixed amount payment types`,
          path: ["bill_rate"],
        });
      }
    }
    return;
  }

  // payment_type omitted:
  // - If serviceTypeID is provided: allow omitting rate/gross_amount (service type defaults will be used)
  // - If serviceTypeID is NOT provided: require payment_type and either rate or gross_amount
  const hasValidRate = data.rate && data.rate > 0;
  const hasValidGrossAmount = data.gross_amount && data.gross_amount > 0;

  if (data.work_info?.serviceTypeID) {
    // If serviceTypeID is provided, allow omitting rate/gross_amount (they'll be inherited)
    // No additional validation needed here
    return;
  }

  // If serviceTypeID is NOT provided, payment_type is required
  if (!data.payment_type) {
    ctx.addIssue({
      code: zSchema.ZodIssueCode.custom,
      message:
        "`payment_type` is required when `work_info.serviceTypeID` is not provided.",
      path: ["payment_type"],
    });
  }

  // If serviceTypeID is NOT provided, require either rate or gross_amount
  if (!hasValidRate && !hasValidGrossAmount) {
    ctx.addIssue({
      code: zSchema.ZodIssueCode.custom,
      message:
        "Either `rate` or `gross_amount` is required and must be greater than 0 when `work_info.serviceTypeID` is not provided.",
      path: ["rate", "gross_amount"],
    });
  }
};

// Base payment item fields (without IDs)
const basePaymentItemFields = {
  work_info: zSchema
    .object({
      serviceTypeID: schemes.mongoOrUuid().optional(),
      workLocationID: schemes.mongoOrUuid().optional(),
      customerAccountID: schemes.uuid().optional(),
      departmentID: zSchema.string().optional(),
      work_description: zSchema
        .string()
        .trim()
        .min(1, "Work description is required")
        .optional(),
      service_date: zSchema.coerce.date().optional(),
    })
    .strict()
    .optional(),
  payment_type: zSchema.nativeEnum(EPaymentItemType).optional(),
  quantity: zSchema
    .number()
    .positive("Quantity must be greater than 0")
    .optional(),
  rate: zSchema.number().positive("Rate must be greater than 0").optional(),
  rate_unit: zSchema.nativeEnum(EServiceTypeRateUnit).optional(),
  gross_amount: zSchema
    .number()
    .positive("Gross amount must be greater than 0")
    .optional(),
  bill_rate: zSchema
    .number()
    .positive("Bill rate must be greater than 0")
    .optional(),
  bill_amount: zSchema
    .number()
    .positive("Bill amount must be greater than 0")
    .optional(),
  metadata: zSchema.record(zSchema.unknown()).optional(),
};

// Base payment item schema without IDs (includes validation)
export const basePaymentItemSchema = zSchema
  .object(basePaymentItemFields)
  .strict()
  .superRefine(paymentItemSuperRefine);

// Full create schema with IDs (includes same validation)
export const createPaymentItemSchema = zSchema
  .object({
    ...basePaymentItemFields,
    companyID: schemes.companyID(),
    contractorID: schemes.mongoOrUuid(),
    contractorPaymentID: schemes.mongoObjectId(),
  })
  .strict()
  .superRefine(paymentItemSuperRefine);

export const updatePaymentItemSchema = zSchema
  .object({
    companyID: schemes.companyID(),
    paymentItemID: schemes.mongoOrUuid(),
    work_info: zSchema
      .object({
        serviceTypeID: schemes.mongoOrUuid().optional(),
        workLocationID: schemes.mongoOrUuid().optional(),
        customerAccountID: schemes.uuid().optional(),
        departmentID: zSchema.string().optional(),
        work_description: zSchema.string().trim().min(1).optional(),
        service_date: zSchema.coerce.date().optional(),
      })
      .strict()
      .optional(),
    payment_type: zSchema.nativeEnum(EPaymentItemType).optional(),
    quantity: zSchema
      .number()
      .positive("Quantity must be greater than 0")
      .optional(),
    rate: zSchema.number().positive("Rate must be greater than 0").optional(),
    rate_unit: zSchema.nativeEnum(EServiceTypeRateUnit).optional(),
    gross_amount: zSchema
      .number()
      .positive("Gross amount must be greater than 0")
      .optional(),
    bill_rate: zSchema
      .number()
      .positive("Bill rate must be greater than 0")
      .optional(),
    bill_amount: zSchema
      .number()
      .positive("Bill amount must be greater than 0")
      .optional(),
    metadata: zSchema.record(zSchema.unknown()).optional(),
  })
  .strict();
